import React from "react"
import types from "prop-types"
import { isEqual } from "lodash-es"
import Rails from "@rails/ujs"
import Formsy from "formsy-react"
import { FormsyHiddenInput } from "shared/inputs"
import { useSelector } from "react-redux"
import {
  includedFormSlugs,
  isMissingRequiredReportBuilderData,
  name,
  orderedSubmissionColumns,
  orderedTaskColumns,
  selectedCategorySlug,
  selectedFormSlug,
  selectedReportType,
  selectedTaskForms,
  shared,
} from "reduxSlices/reportBuilderSlice"
import {
  SOLUTION_REPORT_TYPE,
  SUBMISSION_REPORT_TYPE,
  SURVEY_REPORT_TYPE,
} from "utils/reportHelpers"
import {
  ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH,
  redirectTo,
} from "utils/routeHelpers"
import * as API from "services/api"
import { PrimaryButton, SecondaryButton } from "shared/buttons"
import { errorToast, successToast } from "shared/toast"

const cancel = () => {
  redirectTo(ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH)
}

const updateReport = (reportType, reportId, report) => {
  switch (reportType) {
  case SUBMISSION_REPORT_TYPE:
    return API.updateFormSubmissionReport({ id: reportId, report })
  case SOLUTION_REPORT_TYPE:
    return API.updateSolutionLevelReport({ id: reportId, report })
  case SURVEY_REPORT_TYPE:
    return API.updateSurveyReport({ id: reportId, report })
  default:
    return ""
  }
}

const createReport = (reportType, report) => {
  switch (reportType) {
  case SUBMISSION_REPORT_TYPE:
    return API.createFormSubmissionReport({ report })
  case SOLUTION_REPORT_TYPE:
    return API.createSolutionLevelReport({ report })
  case SURVEY_REPORT_TYPE:
    return API.createSurveyReport({ report })
  default:
    return ""
  }
}

const apiRequest = async (reportType, reportId, report) => {
  if (reportId) {
    return updateReport(reportType, reportId, report)
  }
  return createReport(reportType, report)
}

const ReportBuilderActions = ({ reportId = null }) => {
  const reportType = useSelector(selectedReportType)
  const reportName = useSelector(name)
  const sharedFlag = useSelector(shared)
  const categorySlug = useSelector(selectedCategorySlug)
  const formSlug = useSelector(selectedFormSlug)
  const solutionFormSlugs = useSelector(includedFormSlugs)
  const submissionColumns = useSelector(orderedSubmissionColumns, isEqual)
  const taskForms = useSelector(selectedTaskForms, isEqual)
  const taskColumns = useSelector(orderedTaskColumns, isEqual)

  const disabled = useSelector(isMissingRequiredReportBuilderData)

  const handleSubmit = async (model) => {
    const { report } = model
    const response = await apiRequest(reportType, reportId, report)

    if (response.ok) {
      successToast("Report saved successfully!")
      setTimeout(() => {
        redirectTo(ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH)
      }, 1000)
    } else {
      errorToast("Something went wrong.", response)
    }
  }

  return (
    <div className="mt-6">
      <Formsy
        onSubmit={handleSubmit}
        preventDefaultSubmit
      >
        <FormsyHiddenInput name="authenticity_token" value={Rails.csrfToken()} />
        {
          reportId && (
            <FormsyHiddenInput name="_method" value="put" />
          )
        }
        <FormsyHiddenInput name="report[name]" value={reportName} />
        <FormsyHiddenInput name="report[shared]" value={sharedFlag.toString()} />
        <FormsyHiddenInput name="report[report_setting_attributes][category_slug]" value={categorySlug} />
        {
          formSlug && <FormsyHiddenInput name="report[report_setting_attributes][form_slug]" value={formSlug} />
        }
        {
          reportType === SOLUTION_REPORT_TYPE && solutionFormSlugs.map((slug, index) => (
            <FormsyHiddenInput
              key={`form-slug-${slug}`}
              name={`report[report_setting_attributes][included_form_slugs][${index}]`}
              value={slug}
            />
          ))
        }

        {
          submissionColumns.flatMap(({ type, id }, i) => (
            [
              <FormsyHiddenInput
                key={`submission-columns-type-${type}-${id}`}
                name={`report[report_setting_attributes][submission_columns][${i}][type]`}
                value={type}
              />,
              <FormsyHiddenInput
                key={`submission-columns-key-${type}-${id}`}
                name={`report[report_setting_attributes][submission_columns][${i}][key]`}
                value={id}
              />,
            ]
          ))
        }

        {
          taskForms.flatMap(({ id: slug, questions }, i) => (
            [
              <FormsyHiddenInput
                key={`task-form-slug-${slug}`}
                name={`report[report_setting_attributes][task_forms_data][${i}][slug]`}
                value={slug}
              />,
            ].concat(
              questions.flatMap(({ type, id }, j) => (
                [
                  <FormsyHiddenInput
                    key={`task-question-columns-type-${type}-${id}`}
                    name={`report[report_setting_attributes][task_forms_data][${i}][question_columns][${j}][type]`}
                    value={type}
                  />,
                  <FormsyHiddenInput
                    key={`task-question-columns-key-${type}-${id}`}
                    name={`report[report_setting_attributes][task_forms_data][${i}][question_columns][${j}][key]`}
                    value={id}
                  />,
                ]
              )),
            )
          ))
        }

        {
          taskForms.length > 0
            && taskColumns.flatMap(({ type, id }, i) => (
              [
                <FormsyHiddenInput
                  key={`task-columns-type-${type}-${id}`}
                  name={`report[report_setting_attributes][task_column_defaults][${i}][type]`}
                  value={type}
                />,
                <FormsyHiddenInput
                  key={`task-columns-key-${type}-${id}`}
                  name={`report[report_setting_attributes][task_column_defaults][${i}][key]`}
                  value={id}
                />,
              ]
            ))
        }

        <div className="flex justify-center">
          <SecondaryButton
            className="mr-4"
            onClick={cancel}
            text="Cancel"
          />
          <PrimaryButton
            disabled={disabled}
            text="Save"
            type="submit"
          />
        </div>
      </Formsy>
    </div>
  )
}

ReportBuilderActions.propTypes = {
  reportId: types.number,
}

export default ReportBuilderActions
